/**
 * v3.99an — Compact Forge Index chip.
 * v3.99av — Added ForgeIndexBatchProvider so list/browse pages
 * pre-warm the CACHE with one batched request instead of N round-trips.
 * v3.99av-fix — Eliminated provider/chip race by populating a synchronous
 * PENDING map with the batch promise. Chips now await the in-flight batch
 * before falling back to a single-id fetch.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/apiClient";
import { Gauge } from "lucide-react";

const CACHE = new Map();   // key=`${scope}:${idOrSlug}` → payload
const PENDING = new Map(); // key=`${scope}:${idOrSlug}` → Promise<payload|null>

function tierAccent(s) {
  if (s >= 80) return "var(--gold)";
  if (s >= 60) return "var(--primary-text)";
  if (s >= 40) return "#7CC9FF";
  return "var(--text-muted)";
}

/**
 * Mount once at the top of a list/browse view with the set of strain or
 * breeder keys on screen. Issues a SINGLE batch request and writes every
 * resolved entry into the shared CACHE so each ForgeIndexChip mount is
 * cache-hot.
 *
 * Race-safe: the batch's promise is registered into PENDING synchronously
 * (in the same render commit that mounts the provider). Child chips look
 * up PENDING in their own useEffect and await the same promise instead of
 * firing their own single-id fetch.
 */
export function ForgeIndexBatchProvider({ scope = "strain", keys = [], children }) {
  const sortedKey = useMemo(
    () => Array.from(new Set((keys || []).filter(Boolean))).sort().join(","),
    [keys],
  );

  // Synchronous side-effect during render commit: claim every missing key
  // with the in-flight promise BEFORE any child chip runs its useEffect.
  // React StrictMode invokes this twice in dev — the `if (PENDING.has)` and
  // `if (CACHE.has)` guards make that idempotent.
  if (sortedKey) {
    const uniq = sortedKey.split(",").filter(Boolean);
    const missing = uniq.filter((k) => !CACHE.has(`${scope}:${k}`) && !PENDING.has(`${scope}:${k}`));
    if (missing.length > 0) {
      const promise = (async () => {
        try {
          const { data } = await api.get(`/forge-index/${scope}/batch`, {
            params: { ids: missing.join(",") },
          });
          if (data && typeof data === "object") {
            Object.entries(data).forEach(([k, v]) => {
              CACHE.set(`${scope}:${k}`, v);
            });
          }
        } catch { /* silent */ }
        // Clear PENDING for every key we claimed, hit or miss, so cache-miss
        // chips can fall through to their single-id fetch.
        missing.forEach((k) => PENDING.delete(`${scope}:${k}`));
      })();
      missing.forEach((k) => PENDING.set(`${scope}:${k}`, promise));
    }
  }

  return children;
}

export default function ForgeIndexChip({ scope = "strain", idOrSlug, className = "", testId }) {
  const [data, setData] = useState(() => CACHE.get(`${scope}:${idOrSlug}`) || null);

  useEffect(() => {
    if (!idOrSlug) return;
    const key = `${scope}:${idOrSlug}`;
    if (CACHE.has(key)) { setData(CACHE.get(key)); return; }
    let cancelled = false;
    (async () => {
      // If a batch is in-flight for this key, await it instead of firing
      // a redundant single-id request.
      const inflight = PENDING.get(key);
      if (inflight) {
        await inflight;
        if (cancelled) return;
        if (CACHE.has(key)) { setData(CACHE.get(key)); return; }
        // Batch returned but didn't include this key (deleted strain etc.) —
        // fall through to single-id fetch below.
      }
      try {
        const { data: resp } = await api.get(`/forge-index/${scope}/${encodeURIComponent(idOrSlug)}`);
        CACHE.set(key, resp);
        if (!cancelled) setData(resp);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [scope, idOrSlug]);

  if (!data) return null;
  // Hide when there's literally no supporting evidence — a "FI 0" chip
  // on every empty strain would be visual noise + read negatively.
  const s = data.signals || {};
  if ((s.total_grows || 0) === 0 && (s.review_count || 0) === 0) return null;

  return (
    <span
      title={`Forge Index · ${data.score}/100 — platform authenticity & cultivation-evidence score · ${s.total_grows ?? 0} grow logs · ${s.review_count ?? 0} reviews`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.18em] border whitespace-nowrap ${className}`}
      style={{ color: tierAccent(data.score), borderColor: tierAccent(data.score) }}
      data-testid={testId || `forge-chip-${scope}-${idOrSlug}`}
    >
      <Gauge className="w-2.5 h-2.5 flex-shrink-0" />
      Forge {data.score}
    </span>
  );
}
